"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as s from "@/lib/styles";
import { RequireAuth } from "@/components/require-auth";
import { Spinner, ChartIcon } from "@/lib/icons";
import { apiGet, apiPut, fetchAccounts, type UiAccount } from "@/lib/api";
import { StrategyFlagButtons } from "@/components/strategy-flag-buttons";
import { AccountNicknameField } from "@/components/account-nickname-field";
import { accountNickname, brokerDisplayName, accountBroker } from "@/lib/account-name";
import { BrokerLogo, StrategyPageHeader } from "@/app/strategies/components/strategy-page-header";
import { AnalysisHeaderTable } from "@/app/strategies/components/analysis-header-table";
import { AnalysisPane } from "@/app/strategies/components/analysis-pane";
import { ANALYSIS_START_KEY } from "@/lib/strategy-config-keys";
import {
  ACCOUNT_RETURNS_CHANGED, ACCOUNT_SETTINGS_STRATEGY, fetchAccountReturn, fetchAccountStart, fetchAggregateReturn, fetchBalance,
  fetchCombined, fetchReturnsWindows, peakConcurrentRisk, fetchStartValue, money, pct1, resolveSince, todayET, tone,
  type AccountReturn, type Combined, type ReturnsWindow, type SinceSource,
} from "@/lib/account-returns";
import { ChartModal } from "@/components/chart-modal";
import { PnlChartModal } from "@/components/pnl-chart-modal";
import type { PnlChartTrade } from "@/components/pnl-chart";
import { type SpreadChartOverlay } from "@/components/price-chart";
import { useToast } from "@/components/toast-provider";
import { ColumnTable, fmtMDY, type TableColumn } from "@/app/trades/summary-columns";
import { OpenPositions } from "./open-positions";

interface Row {
  key: string;
  account: UiAccount;
  name: string;
  broker: string;
  /** Effective window start: the account-level date, else the earliest strategy date, else the earliest trade. */
  since: string | null;
  sinceSource: SinceSource | null;
  /** The account-level date saved on this page ("" when none). */
  accountStart: string;
  startValue: number | null;
  combined: Combined | null;
  /** The app's reconstructed account value today. */
  balance: number | null;
  /** Time-weighted return from the window start to today. */
  ret: AccountReturn | null;
  loading: boolean;
  error: string | null;
}

const th = (label: string, title?: string) => (
  <th className="py-1 pr-4 underline text-center" title={title}>{label}</th>
);
const alignClass = { left: "text-left", center: "text-center", right: "text-right" } as const;
const td = (node: React.ReactNode, align: keyof typeof alignClass = "right", cls = "", title?: string) => (
  <td className={`py-1 pr-4 ${alignClass[align]} font-mono ${cls}`} title={title}>{node}</td>
);
const dash = <span className="text-muted">—</span>;

// What each column means — shown as a tooltip on the header and on every amount.
const TIP = {
  since: "The “Returns since” date set in this account’s row. If none is set, the earliest “Returns since” date saved on any of this account’s strategies; if none is saved, the account’s earliest trade.",
  start: "Account value at the close of the “Returns since” date: cash + stock value − open option premium.",
  pnl: "Realized profit of every trade closed since the “Returns since” date: spreads, single options, and stock, fees included. Deposits, withdrawals, dividends, and interest are not included.",
  today: "Realized profit of every trade closed today (Eastern time). Open positions are not marked to market.",
  ret: "The account’s time-weighted return since the “Returns since” date: the window is cut at every deposit or withdrawal, each piece is measured on the money actually in the account, and the pieces are chained. Money moved in or out never counts as gain or loss. Measured on Balance, so stock, dividends, interest, and fees are all in.",
  ann: "Total return compounded to one year over the calendar days from the “Returns since” date to today.",
  balance: "The app’s reconstruction of the account’s value today, the same way Start value is computed: cash from every recorded trade (prices are net of fees), deposit, withdrawal, dividend, and interest, plus stock at the last close and open options at their premium. Compare it with the broker’s balance to spot anything missing.",
};

const hasTrades = (r: Row) => !!r.combined && r.combined.nTrades > 0;
const pnlOf = (r: Row) => (hasTrades(r) ? r.combined!.totalPnl : null);
const todayPnlOf = (r: Row) => (hasTrades(r) ? r.combined!.todayPnl : null);
const returnOf = (r: Row) => r.ret?.total ?? null;
const annualizedOf = (r: Row) => r.ret?.annualized ?? null;

/** Tooltip detail: each piece of the time-weighted return. */
function piecesTip(r: Row): string {
  if (!r.ret || r.ret.pieces.length < 2) return "";
  return " Pieces: " + r.ret.pieces.map((p) => `${fmtMDY(p.from)}–${fmtMDY(p.to)} ${pct1(p.ret)}`).join(", ") + ".";
}

const HIDDEN_PANES_KEY = "accounts:hiddenPanes";

/** Each broker's web site — the account name links there (paper accounts have none). */
const BROKER_SITE_URL: Record<string, string> = {
  tastytrade: "https://my.tastytrade.com/app.html#/dashboard/",
  schwab: "https://client.schwab.com/app/accounts/positions/#/",
};

export default function AccountsPage() {
  const { showToast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [accounts, setAccounts] = useState<UiAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Strategy flags: "accountHash:strategy" -> enabled
  const [strategyFlags, setStrategyFlags] = useState<Record<string, boolean>>({});

  // One collapsible Analysis section per account, remembered like the strategy pages' panes.
  const [hiddenPanes, setHiddenPanes] = useState<Set<string>>(new Set());
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HIDDEN_PANES_KEY);
      setHiddenPanes(stored ? new Set(JSON.parse(stored)) : new Set());
    } catch { setHiddenPanes(new Set()); }
  }, []);
  const togglePane = (key: string) => setHiddenPanes((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    try { localStorage.setItem(HIDDEN_PANES_KEY, JSON.stringify([...next])); } catch {}
    return next;
  });
  const paneHeader = (key: string, label: string, trailing?: React.ReactNode) => (
    <h2
      className="text-xs font-semibold text-amber py-1.5 px-2 bg-amber/10 cursor-pointer select-none flex items-center gap-1.5"
      onClick={() => togglePane(key)}
    >
      <span className="text-amber/50 text-[10px]">{hiddenPanes.has(key) ? "▶" : "▼"}</span>
      {label}
      {trailing}
    </h2>
  );

  // Chart modal for the per-trade ledgers (one modal, whichever account opened it).
  const [chartSymbol, setChartSymbol] = useState<string | null>(null);
  const [chartSpreadOverlay, setChartSpreadOverlay] = useState<SpreadChartOverlay | null>(null);
  const [chartTimeframe, setChartTimeframe] = useState<"1D" | "5m" | undefined>(undefined);
  const [chartAccount, setChartAccount] = useState<string | undefined>(undefined);
  const openChartFor = useCallback((accountHash: string) => (sym: string, spreadOverlay: SpreadChartOverlay | null) => {
    setChartAccount(accountHash);
    setChartSpreadOverlay(spreadOverlay);
    setChartSymbol(sym);
    // A 0-DTE spread (expires today, ET) opens on the 5-minute intraday chart;
    // everything else keeps the daily default.
    const exp = spreadOverlay?.expirationDate;
    setChartTimeframe(exp && exp.slice(0, 10) === todayET() ? "5m" : undefined);
  }, []);
  // Profit/(loss)-over-time modal: the account's closed trades as of the click.
  const [pnlChart, setPnlChart] = useState<{ name: string; since: string; trades: PnlChartTrade[] } | null>(null);
  const closeChart = useCallback(() => {
    setChartSymbol(null);
    setChartSpreadOverlay(null);
    setChartTimeframe(undefined);
    setChartAccount(undefined);
  }, []);

  useEffect(() => {
    apiGet<{ accountHash: string; strategy: string; enabled: boolean }[]>("/strategies/account-flags")
      .then((flags) => {
        const m: Record<string, boolean> = {};
        for (const f of flags) m[`${f.accountHash}:${f.strategy}`] = f.enabled;
        setStrategyFlags(m);
      })
      .catch(() => {});
  }, []);

  const toggleStrategyFlag = useCallback(async (accountHash: string, strategy: string) => {
    const key = `${accountHash}:${strategy}`;
    const currentlyEnabled = strategyFlags[key] ?? false;
    const newEnabled = !currentlyEnabled;
    setStrategyFlags((prev) => ({ ...prev, [key]: newEnabled }));
    try {
      await apiPut("/strategies/account-flags", { accountHash, strategy, enabled: newEnabled });
      // Notify the Strategies menu + left-pane links to refresh their lists.
      window.dispatchEvent(new CustomEvent("strategy-flags-changed"));
    } catch {
      setStrategyFlags((prev) => ({ ...prev, [key]: currentlyEnabled }));
    }
  }, [strategyFlags]);

  const patchRow = useCallback((key: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  }, []);

  /** Load (or reload) one account's start value and merged analysis for its window. */
  const loadAccount = useCallback((key: string, hash: string, since: string, quiet = false) => {
    if (!quiet) patchRow(key, { loading: true, error: null });
    Promise.all([fetchStartValue(hash, since), fetchCombined(hash, since), fetchBalance(hash)])
      .then(async ([startValue, combined, balance]) => {
        const ret = await fetchAccountReturn(hash, since, startValue, balance);
        patchRow(key, { startValue, combined, balance, ret, loading: false });
      })
      .catch((e) => {
        if (quiet) return; // keep the last good numbers
        patchRow(key, { loading: false, error: e instanceof Error ? e.message : String(e) });
      });
  }, [patchRow]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchAccounts(), fetchReturnsWindows()])
      .then(async ([accounts, byHash]) => {
        if (cancelled) return;
        const starts = await Promise.all(
          accounts.map((a) => fetchAccountStart(a.hashValue).catch(() => "")),
        );
        if (cancelled) return;
        const initial: Row[] = accounts.map((a, i) => {
          const accountStart = starts[i];
          const { since, sinceSource } = resolveSince(accountStart, byHash.get(a.hashValue));
          return {
            key: a.hashValue,
            account: a,
            name: accountNickname(a),
            broker: brokerDisplayName(accountBroker(a)),
            since,
            sinceSource,
            accountStart,
            startValue: null,
            combined: null,
            balance: null,
            ret: null,
            loading: !!since,
            error: null,
          };
        });
        setRows(initial);
        setAccounts(accounts);
        setLoading(false);

        // Fill each account independently so one slow or failing call never
        // blanks the whole table.
        for (const r of initial) {
          if (r.since) loadAccount(r.key, r.account.hashValue, r.since);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [loadAccount]);

  // Silent refresh so fills show up without a reload (the strategy pages poll the same way).
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      setRows((prev) => {
        for (const r of prev) {
          if (r.since && !r.loading) loadAccount(r.key, r.account.hashValue, r.since, true);
        }
        return prev;
      });
    }, 30_000);
    return () => clearInterval(id);
  }, [loadAccount]);

  /** Save the account-level "Returns since" date and recompute that account. */
  const updateAccountStart = useCallback((r: Row, v: string) => {
    const since = v || null;
    patchRow(r.key, { accountStart: v, since, sinceSource: v ? "account" : null, combined: null, startValue: null, balance: null, ret: null });
    apiPut(`/strategies/account-settings/${ACCOUNT_SETTINGS_STRATEGY}`, {
      accountHash: r.account.hashValue,
      entries: [{ key: ANALYSIS_START_KEY, value: v }],
    })
      .then(() => {
        // The sidebar's Accounts section shows the same return — refresh it.
        window.dispatchEvent(new CustomEvent(ACCOUNT_RETURNS_CHANGED));
        if (since) {
          loadAccount(r.key, r.account.hashValue, since);
          return;
        }
        // Cleared: fall back to the strategies' earliest date or the earliest trade.
        apiGet<ReturnsWindow[]>("/accounts/returns-window")
          .then((windows) => {
            const w = windows.find((x) => x.accountHash === r.account.hashValue);
            patchRow(r.key, { since: w?.since ?? null, sinceSource: w?.sinceSource ?? null });
            if (w) loadAccount(r.key, r.account.hashValue, w.since);
          })
          .catch(() => {});
      })
      .catch(() => showToast("error", "Failed to save the Returns since date"));
  }, [patchRow, loadAccount, showToast]);

  // All accounts together, for the returns box in the page header. Waits until
  // every account with a window has loaded so the sums are never partial.
  const valueCache = useRef(new Map<string, number>());
  const [allReturn, setAllReturn] = useState<AccountReturn | null>(null);
  const windowed = rows.filter((r) => r.since);
  const allLoaded = windowed.length > 0 && windowed.every((r) => !r.loading);
  const allSince = windowed.reduce((m, r) => (!m || r.since! < m ? r.since! : m), "");
  const allAnalysis = useMemo(() => {
    if (!allLoaded) return null;
    const trades = windowed.flatMap((r) => r.combined?.trades ?? []);
    return {
      nTrades: trades.length,
      nWins: trades.filter((t) => t.earlyPnl > 0).length,
      totalPnl: trades.reduce((sum, t) => sum + t.earlyPnl, 0),
      totalCapitalAtRisk: peakConcurrentRisk(trades),
      trades,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, allLoaded]);
  useEffect(() => {
    if (!allLoaded) return;
    const members = windowed
      .filter((r) => r.startValue != null && r.balance != null)
      .map((r) => ({ hash: r.account.hashValue, since: r.since!, startValue: r.startValue!, balance: r.balance! }));
    let cancelled = false;
    fetchAggregateReturn(members, valueCache.current)
      .then((ret) => { if (!cancelled) setAllReturn(ret); })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, allLoaded]);

  const oc = (
    id: string,
    header: React.ReactNode,
    cell: (r: Row) => React.ReactNode,
    sortValue?: (r: Row) => number | string | null | undefined,
  ): TableColumn<Row> => ({
    id, header: () => header, cell,
    ...(sortValue ? { sortKey: id, sortValue } : {}),
  });

  const num = (r: Row, v: number | null, fmt: (n: number) => string, title: string, colored = true) => {
    if (r.loading) return td(<Spinner className="inline-flex w-3 h-3 text-muted" />);
    if (v == null) return td(dash, "right", "", title);
    return td(fmt(v), "right", colored ? tone(v) : "", title);
  };

  const sinceTip = (r: Row) =>
    r.sinceSource === "account"
      ? "The “Returns since” date set in this account’s row."
      : r.sinceSource === "trades"
        ? "No “Returns since” date is set for this account or saved on any of its strategies, so this is its earliest trade."
        : TIP.since;

  const columns: TableColumn<Row>[] = [
    oc("account", th("Account"), (r) => (
      <td className="py-1 pr-4 text-left">
        <span className="inline-flex items-center gap-2">
          <BrokerLogo account={r.account} className="h-5 w-5 object-contain rounded shrink-0" />
          {BROKER_SITE_URL[accountBroker(r.account)] ? (
            <a
              href={BROKER_SITE_URL[accountBroker(r.account)]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-cyan underline"
              title={`Open this account at ${r.broker} (new tab)`}
            >
              {r.name}
            </a>
          ) : (
            <span>{r.name}</span>
          )}
        </span>
      </td>
    ), (r) => r.name),
    oc("broker", th("Broker"), (r) => td(r.broker, "left"), (r) => r.broker),
    oc("nickname", th("Nickname", "Push notifications use this instead of the account name."), (r) => (
      <td className="py-1 pr-4 text-left">
        <AccountNicknameField
          accountHash={r.account.hashValue}
          value={r.account.notifyNickname}
          placeholder={r.name}
          onSaved={(next) => {
            setRows((prev) => prev.map((p) => (p.key === r.key ? { ...p, account: { ...p.account, notifyNickname: next || null } } : p)));
          }}
        />
      </td>
    )),
    oc("strategies", th("Strategies", "Which strategies may trade this account. A strategy lists the account in its dropdown only when it is turned on here."), (r) => (
      <td className="py-1 pr-4 text-left">
        <StrategyFlagButtons accountHash={r.account.hashValue} flags={strategyFlags} onToggle={toggleStrategyFlag} />
      </td>
    )),
    oc("since", th("Returns since", TIP.since), (r) => (
      <td className="py-1 pr-4 text-center" title={sinceTip(r)}>
        <span className="inline-flex items-center gap-1">
          <input
            type="date"
            value={r.since ?? ""}
            onChange={(e) => updateAccountStart(r, e.target.value)}
            className="bg-transparent border border-border rounded px-1.5 py-0 text-xs text-foreground font-mono"
          />
          {r.sinceSource === "account" && (
            <button
              onClick={() => updateAccountStart(r, "")}
              className="text-muted hover:text-foreground px-1"
              title="Clear — fall back to the strategies’ dates"
            >
              ✕
            </button>
          )}
        </span>
      </td>
    ), (r) => r.since ?? undefined),
    oc("start", th("Start value", TIP.start), (r) => num(r, r.startValue, money, TIP.start, false), (r) => r.startValue),
    oc("pnl", th("Profit/(loss)", TIP.pnl), (r) => {
      const v = pnlOf(r);
      if (r.loading || v == null) return num(r, v, money, TIP.pnl);
      return td(
        <>
          <span title={TIP.pnl}>{money(v)}</span>
          <button
            onClick={() => setPnlChart({ name: r.name, since: r.since ?? "", trades: r.combined!.trades })}
            className="inline-flex items-center justify-center p-0 m-0 leading-none align-middle ml-2 text-muted hover:text-cyan"
            title="Chart profit/(loss) over time"
          >
            <ChartIcon />
          </button>
        </>,
        "right",
        tone(v),
      );
    }, (r) => pnlOf(r)),
    oc("today", th("Profit/(loss) today", TIP.today), (r) => num(r, todayPnlOf(r), money, TIP.today), (r) => todayPnlOf(r)),
    oc("return", th("Total return", TIP.ret), (r) => num(r, returnOf(r), pct1, TIP.ret + piecesTip(r)), (r) => returnOf(r)),
    oc("annualized", th("Annualized return", TIP.ann), (r) => {
      const days = r.ret?.days ?? 0;
      return num(r, annualizedOf(r), pct1, days > 0 ? `${TIP.ann} Here that is ${days} days.` : TIP.ann);
    }, (r) => annualizedOf(r)),
    oc("balance", th("Balance", TIP.balance), (r) => num(r, r.balance, money, TIP.balance, false), (r) => r.balance),
  ];

  return (
    <RequireAuth>
      <div className="pb-6 px-4 min-w-0">
        <StrategyPageHeader
          title="Accounts"
          analysis={
            <AnalysisHeaderTable
              analysis={allAnalysis}
              analysisStart={allSince}
              origSize={null}
              accountValue={allLoaded ? rows.reduce((sum, r) => sum + (r.balance ?? 0), 0) : null}
              returns={{ total: allReturn?.total ?? null, annualized: allReturn?.annualized ?? null }}
              sinceTitle="All accounts together. Each account counts from its own “Returns since” date; this is the earliest of them. Total return is time-weighted over the combined value of every account: an account joining later counts like a deposit, and deposits and withdrawals never count as gain or loss."
            />
          }
        />
        <div className="rounded-lg border border-border bg-surface px-3 pb-2 overflow-x-auto">
          {loading ? (
            <div className="flex items-center gap-2 py-3 text-sm text-muted">
              <Spinner className={`${s.statusIcon} text-muted`} /> Loading accounts…
            </div>
          ) : error ? (
            <div className="py-3 text-sm text-red">{error}</div>
          ) : rows.length === 0 ? (
            <div className="py-3 text-sm text-muted">No accounts.</div>
          ) : (
            <ColumnTable<Row>
              columns={columns}
              rows={rows}
              expanded
              defaultSortKey="account"
              defaultSortAsc
              tableClassName="text-xs whitespace-nowrap"
              headRowClassName="text-center text-amber border-b border-border"
              renderRow={(r, cells) => (
                <React.Fragment key={r.key}>
                  <tr className="border-b border-border/50 hover:bg-surface-light" title={r.error ?? undefined}>
                    {cells}
                  </tr>
                  {/* Analysis — every strategy's closed trades in the account, merged; sits right under its row. */}
                  {r.since && (
                    <tr className="border-b border-border/50">
                      <td colSpan={columns.length} className="p-0 pb-3 whitespace-normal">
                        {paneHeader(r.key, "Analysis")}
                        {!hiddenPanes.has(r.key) && (
                          <>
                            <AnalysisPane
                              analysis={r.combined}
                              loading={r.loading}
                              error={r.error}
                              analysisStart={r.since ?? ""}
                              origSize={r.startValue}
                              openChart={openChartFor(r.account.hashValue)}
                              emptyText="No closed trades yet to analyze."
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )}
            />
          )}
        </div>
        {/* Every open position in every account, from each broker. */}
        {accounts.length > 0 && (
          <div className="mt-4 rounded-lg border border-border bg-surface px-3 pb-3 overflow-x-auto">
            <OpenPositions
              accounts={accounts}
              openChart={openChartFor}
              header={(trailing) => paneHeader("openPositions", "Open Positions", trailing)}
              hidden={hiddenPanes.has("openPositions")}
            />
          </div>
        )}
      </div>
      <ChartModal
        symbol={chartSymbol}
        spreadOverlay={chartSpreadOverlay}
        onClose={closeChart}
        zIndex={80}
        defaultTimeframe={chartTimeframe}
        accountHash={chartAccount}
      />
      <PnlChartModal account={pnlChart} onClose={() => setPnlChart(null)} zIndex={80} />
    </RequireAuth>
  );
}
